import {
  ApiError,
  factoriesControllerArchiveFactory,
  factoriesControllerArchiveStation,
  factoriesControllerArchiveZone,
  factoriesControllerCreateFactory,
  factoriesControllerCreateStation,
  factoriesControllerCreateZone,
  factoriesControllerGetZones,
  factoriesControllerListFactories,
  factoriesControllerRenameZone,
  factoriesControllerUnarchiveFactory,
  factoriesControllerUnarchiveStation,
  factoriesControllerUnarchiveZone,
  factoriesControllerUpdateFactory,
  factoriesControllerUpdateStation,
  useCurrentUser,
} from '@artic/api-client';
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { expectData } from '../_shared/api';

export const FACTORY_ADMIN_KEY = 'factory-admin' as const;

// ── Response shapes (mirror the nest-backend factory-admin DTOs) ──────────────

export interface FactoryListItem {
  factory_id: string;
  factory_name: string | null;
  factory_timezone: string;
  is_archived?: boolean;
}

export interface StationNode {
  station_id: string;
  station_name: string;
  is_archived: boolean;
}

export interface ZoneNode {
  zone_id: string;
  zone_name: string | null;
  is_archived: boolean;
  stations: StationNode[];
}

export interface FactoryTopology {
  factory_id: string;
  factory_name: string | null;
  factory_timezone: string;
  is_archived: boolean;
  zones: ZoneNode[];
}

export interface Factory {
  factory_id: string;
  factory_name: string | null;
  factory_timezone: string;
  is_archived: boolean;
}

// ── Admin gate (factory create/edit/archive is Artic-admin only) ──────────────

/** Mirrors routes/admin/route.tsx + nest-backend isAdminUser (exact domain). */
export function useIsArticAdmin(): boolean {
  const { user } = useCurrentUser();
  const domain = user.email.toLowerCase().split('@').at(-1);
  return domain === 'artic.works' || domain === 'trunk.works';
}

// ── Conflict surface (409 → field-pinned error) ───────────────────────────────

export type ConflictField = 'factory_name' | 'zone_name' | 'station_name';

export class FactoryAdminConflictError extends Error {
  public readonly field: ConflictField;

  constructor(field: ConflictField, message: string) {
    super(message);
    this.name = 'FactoryAdminConflictError';
    this.field = field;
  }
}

function mapConflict(err: unknown): never {
  if (err instanceof ApiError && err.status === 409) {
    const body = err.bodyAsRecord;
    const rawField = body?.['field'];
    const rawMessage = body?.['message'];
    const message = typeof rawMessage === 'string' ? rawMessage : err.message;
    if (rawField === 'factory_name' || rawField === 'zone_name' || rawField === 'station_name') {
      throw new FactoryAdminConflictError(rawField, message);
    }
  }
  throw err;
}

// ── Queries ───────────────────────────────────────────────────────────────────

export function factoriesListQueryOptions(partnerId: string | undefined) {
  return queryOptions({
    queryKey: [FACTORY_ADMIN_KEY, 'list', partnerId] as const,
    queryFn: async ({ signal }) => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await factoriesControllerListFactories({ partner_id: partnerId }, { signal });
      return expectData<FactoryListItem[]>(res);
    },
    enabled: !!partnerId,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function factoryTopologyQueryOptions(factoryId: string | undefined) {
  return queryOptions({
    queryKey: [FACTORY_ADMIN_KEY, 'topology', factoryId] as const,
    queryFn: async ({ signal }) => {
      if (!factoryId) throw new Error('factoryId is required');
      const res = await factoriesControllerGetZones(factoryId, { signal });
      return expectData<FactoryTopology>(res);
    },
    enabled: !!factoryId,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────────
//
// Each mutation invalidates the affected factory's topology and the partner's
// factory list. The view records the touched id in a "recently edited" window so
// the realtime echo of the user's own change isn't re-animated (see
// use-factory-admin-channel.ts).

function useInvalidate() {
  const queryClient = useQueryClient();
  return {
    list: (partnerId: string | undefined) =>
      void queryClient.invalidateQueries({ queryKey: [FACTORY_ADMIN_KEY, 'list', partnerId] }),
    topology: (factoryId: string) =>
      void queryClient.invalidateQueries({ queryKey: [FACTORY_ADMIN_KEY, 'topology', factoryId] }),
  };
}

export function useCreateFactory(partnerId: string | undefined) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: { factory_name: string; factory_timezone: string }) => {
      if (!partnerId) throw new Error('partnerId is required');
      try {
        const res = await factoriesControllerCreateFactory({
          partner_id: partnerId,
          ...input,
        });
        return expectData<Factory>(res);
      } catch (err) {
        mapConflict(err);
      }
    },
    onSuccess: () => invalidate.list(partnerId),
  });
}

export function useUpdateFactory(partnerId: string | undefined, factoryId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: { factory_name?: string; factory_timezone?: string }) => {
      try {
        const res = await factoriesControllerUpdateFactory(factoryId, input);
        return expectData<Factory>(res);
      } catch (err) {
        mapConflict(err);
      }
    },
    onSuccess: () => {
      invalidate.list(partnerId);
      invalidate.topology(factoryId);
    },
  });
}

export function useSetFactoryArchived(partnerId: string | undefined, factoryId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (archived: boolean) => {
      if (archived) {
        await factoriesControllerArchiveFactory(factoryId);
      } else {
        await factoriesControllerUnarchiveFactory(factoryId);
      }
    },
    onSuccess: () => invalidate.list(partnerId),
  });
}

export function useCreateZone(factoryId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: { zone_name: string }) => {
      try {
        const res = await factoriesControllerCreateZone(factoryId, input);
        return expectData<ZoneNode>(res);
      } catch (err) {
        mapConflict(err);
      }
    },
    onSuccess: () => invalidate.topology(factoryId),
  });
}

export function useRenameZone(factoryId: string, zoneId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: { new_zone_name: string }) => {
      try {
        await factoriesControllerRenameZone(factoryId, zoneId, input);
      } catch (err) {
        mapConflict(err);
      }
    },
    onSuccess: () => invalidate.topology(factoryId),
  });
}

export function useSetZoneArchived(factoryId: string, zoneId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (archived: boolean) => {
      if (archived) {
        await factoriesControllerArchiveZone(factoryId, zoneId);
      } else {
        await factoriesControllerUnarchiveZone(factoryId, zoneId);
      }
    },
    onSuccess: () => invalidate.topology(factoryId),
  });
}

export function useCreateStation(factoryId: string, zoneId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: { station_name: string }) => {
      try {
        const res = await factoriesControllerCreateStation(factoryId, zoneId, input);
        return expectData<StationNode>(res);
      } catch (err) {
        mapConflict(err);
      }
    },
    onSuccess: () => invalidate.topology(factoryId),
  });
}

export function useUpdateStation(factoryId: string, zoneId: string, stationId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: { new_station_name?: string; new_zone_id?: string }) => {
      try {
        await factoriesControllerUpdateStation(factoryId, zoneId, stationId, input);
      } catch (err) {
        mapConflict(err);
      }
    },
    onSuccess: () => invalidate.topology(factoryId),
  });
}

export function useSetStationArchived(factoryId: string, zoneId: string, stationId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (archived: boolean) => {
      if (archived) {
        await factoriesControllerArchiveStation(factoryId, zoneId, stationId);
      } else {
        await factoriesControllerUnarchiveStation(factoryId, zoneId, stationId);
      }
    },
    onSuccess: () => invalidate.topology(factoryId),
  });
}
